import { useSelector, useDispatch } from "react-redux";
import { useCallback } from "react";
import {
  loginUser,
  signupUser,
  fetchCurrentUser,
  logout,
  clearError,
  selectUser,
  selectAuthLoading,
  selectAuthError,
  selectIsAuthenticated,
} from "./authSlice";

const useAuth = () => {
  const dispatch = useDispatch();

  const user = useSelector(selectUser);
  const loading = useSelector(selectAuthLoading);
  const error = useSelector(selectAuthError);
  const isAuthenticated = useSelector(selectIsAuthenticated);

  const login = useCallback(
    async (credentials) => {
      const result = await dispatch(loginUser(credentials));
      if (loginUser.fulfilled.match(result)) {
        return { success: true, user: result.payload.user };
      } else {
        return { success: false, error: result.payload };
      }
    },
    [dispatch]
  );

  const signup = useCallback(
    async (userData) => {
      const result = await dispatch(signupUser(userData));
      if (signupUser.fulfilled.match(result)) {
        return { success: true, user: result.payload.user };
      } else {
        return { success: false, error: result.payload };
      }
    },
    [dispatch]
  );

  const refreshUser = useCallback(async () => {
    const result = await dispatch(fetchCurrentUser());
    return fetchCurrentUser.fulfilled.match(result);
  }, [dispatch]);

  const signOut = useCallback(() => {
    dispatch(logout());
  }, [dispatch]);

  const clearAuthError = useCallback(() => {
    dispatch(clearError());
  }, [dispatch]);

  const hasRole = useCallback(
    (role) => user?.role === role,
    [user]
  );

  const hasAnyRole = useCallback(
    (roles) => roles.includes(user?.role),
    [user]
  );

  // Kept for backward compat — returns empty object since cookies handle auth
  const getAuthHeader = useCallback(() => ({}), []);

  return {
    user,
    token: null,
    loading,
    error,
    isAuthenticated,

    login,
    signup,
    refreshUser,
    logout: signOut,
    clearError: clearAuthError,

    hasRole,
    hasAnyRole,
    getAuthHeader,
  };
};

export default useAuth;
